import {
  generationSchema,
  generationsConfigSchema,
  uploadSchema,
  meSchema,
  paymentSchema,
  paymentsConfigSchema,
  trendCardSchema,
  trendDetailSchema,
  twinSchema,
  livenessResultSchema,
  partnerSummarySchema,
  partnerPayoutSchema,
  duoInviteSchema,
  type CreateDuoRequest,
  type DuoInvite,
  type PartnerPayout,
  type PartnerSummary,
  type PayoutRequest,
  type UserSettings,
  type CreatePhotoRequest,
  type CreateSongRequest,
  type CreateVideoRequest,
  type LivenessPose,
  type LivenessResult,
  type Twin,
  type CreateTrendRequest,
  type TrendCard,
  type TrendDetail,
  type Generation,
  type GenerationsConfig,
  type Me,
  type Upload,
  type Payment,
  type PaymentMethod,
  type PaymentsConfig,
  type ClientEventsRequest,
  type ReportRequest,
  MODERATION_TEXTS,
} from '@trendator/shared';
import { getInitData } from './telegram';

/** Ручной режим или тренд. */
export type CreateGenerationRequest = CreatePhotoRequest | CreateTrendRequest | CreateVideoRequest | CreateSongRequest;

const BASE = (import.meta.env.VITE_API_URL as string | undefined) ?? '/api';

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly code: string,
    /** Тело ошибки (например, balance/price при 402). */
    public readonly data: Record<string, unknown> = {},
  ) {
    super(`API ${status}: ${code}`);
  }
}

/** Отказ модерации → текст для юзера (сервер присылает его же в message). */
const MODERATION_CODES = ['prompt_blocked', 'nsfw_input', 'minor_not_allowed'] as const;
export function moderationMessage(err: unknown): string | null {
  if (!(err instanceof ApiError) || !(MODERATION_CODES as readonly string[]).includes(err.code)) return null;
  const msg = err.data.message;
  return typeof msg === 'string' && msg ? msg : MODERATION_TEXTS[err.code as (typeof MODERATION_CODES)[number]];
}

async function request(path: string, init: RequestInit = {}): Promise<unknown> {
  const initData = getInitData();
  if (!initData) throw new ApiError(401, 'no_init_data');
  const res = await fetch(`${BASE}${path}`, {
    ...init,
    headers: {
      authorization: `tma ${initData}`,
      ...(typeof init.body === 'string' ? { 'content-type': 'application/json' } : {}),
      ...init.headers,
    },
  });
  const data: unknown = await res.json().catch(() => null);
  if (!res.ok) {
    const body = (data ?? {}) as Record<string, unknown>;
    throw new ApiError(res.status, typeof body.error === 'string' ? body.error : 'http_error', body);
  }
  return data;
}

export const api = {
  /** Вход: создаёт юзера при первом заходе, учитывает рефку из start_param. */
  auth: async (): Promise<Me> => meSchema.parse(await request('/v1/auth', { method: 'POST' })),
  me: async (): Promise<Me> => meSchema.parse(await request('/v1/me')),
  consent: async (): Promise<Me> =>
    meSchema.parse(await request('/v1/me/consent', { method: 'POST', body: JSON.stringify({ adult: true, rules: true }) })),
  paymentsConfig: async (): Promise<PaymentsConfig> => paymentsConfigSchema.parse(await request('/v1/payments/config')),
  createPayment: async (packageId: string, method: PaymentMethod): Promise<Payment> =>
    paymentSchema.parse(await request('/v1/payments', { method: 'POST', body: JSON.stringify({ packageId, method }) })),
  payment: async (id: string): Promise<Payment> => paymentSchema.parse(await request(`/v1/payments/${encodeURIComponent(id)}`)),
  generationsConfig: async (): Promise<GenerationsConfig> =>
    generationsConfigSchema.parse(await request('/v1/generations/config')),
  createGeneration: async (body: CreateGenerationRequest): Promise<Generation> =>
    generationSchema.parse(await request('/v1/generations', { method: 'POST', body: JSON.stringify(body) })),
  generation: async (id: string): Promise<Generation> =>
    generationSchema.parse(await request(`/v1/generations/${encodeURIComponent(id)}`)),
  generations: async (): Promise<Generation[]> =>
    ((await request('/v1/generations')) as { items: unknown[] }).items.map((i) => generationSchema.parse(i)),
  trends: async (): Promise<TrendCard[]> =>
    ((await request('/v1/trends')) as { trends: unknown[] }).trends.map((t) => trendCardSchema.parse(t)),
  trend: async (idOrSlug: string): Promise<TrendDetail> =>
    trendDetailSchema.parse(await request(`/v1/trends/${encodeURIComponent(idOrSlug)}`)),
  twin: async (): Promise<Twin> => twinSchema.parse(await request('/v1/twin')),
  twinUpload: async (file: Blob): Promise<Twin> => {
    const form = new FormData();
    form.append('file', file);
    return twinSchema.parse(await request('/v1/twin/photos', { method: 'POST', body: form }));
  },
  twinDeletePhoto: async (id: string): Promise<Twin> =>
    twinSchema.parse(await request(`/v1/twin/photos/${encodeURIComponent(id)}`, { method: 'DELETE' })),
  deleteTwin: async (): Promise<Twin> => twinSchema.parse(await request('/v1/twin', { method: 'DELETE' })),
  liveness: async (pose: LivenessPose, frame: Blob): Promise<LivenessResult> => {
    const form = new FormData();
    form.append('file', frame, `${pose}.jpg`);
    return livenessResultSchema.parse(await request(`/v1/twin/liveness/${pose}`, { method: 'POST', body: form }));
  },
  /** Своё фото двойника: картинка приходит только с авторизацией — отдаём blob-ссылку. */
  twinPhotoUrl: async (id: string): Promise<string> => {
    const initData = getInitData();
    if (!initData) throw new ApiError(401, 'no_init_data');
    const res = await fetch(`${BASE}/v1/twin/photos/${encodeURIComponent(id)}/image`, { headers: { authorization: `tma ${initData}` } });
    if (!res.ok) throw new ApiError(res.status, 'http_error');
    return URL.createObjectURL(await res.blob());
  },
  updateSettings: async (notify: Partial<UserSettings['notify']>): Promise<Me> =>
    meSchema.parse(await request('/v1/me/settings', { method: 'PATCH', body: JSON.stringify({ notify }) })),
  /** «Удалить все мои данные». */
  forget: async (): Promise<{ deleted: number }> => (await request('/v1/me/forget', { method: 'POST' })) as { deleted: number },
  partner: async (): Promise<PartnerSummary> => partnerSummarySchema.parse(await request('/v1/partner')),
  partnerTransfer: async (amount: number, key: string): Promise<{ credited: number; balance: number; partner: PartnerSummary }> => {
    const r = (await request('/v1/partner/transfer', { method: 'POST', body: JSON.stringify({ amount, key }) })) as { credited: number; balance: number; partner: unknown };
    return { credited: r.credited, balance: r.balance, partner: partnerSummarySchema.parse(r.partner) };
  },
  partnerPayout: async (body: PayoutRequest): Promise<{ payout: PartnerPayout; partner: PartnerSummary }> => {
    const r = (await request('/v1/partner/payouts', { method: 'POST', body: JSON.stringify(body) })) as { payout: unknown; partner: unknown };
    return { payout: partnerPayoutSchema.parse(r.payout), partner: partnerSummarySchema.parse(r.partner) };
  },
  setPostback: async (url: string | null): Promise<PartnerSummary> =>
    partnerSummarySchema.parse(await request('/v1/partner/postback', { method: 'PUT', body: JSON.stringify({ url }) })),
  createDuo: async (body: CreateDuoRequest): Promise<DuoInvite> => duoInviteSchema.parse(await request('/v1/duo', { method: 'POST', body: JSON.stringify(body) })),
  duo: async (code: string): Promise<DuoInvite> => duoInviteSchema.parse(await request(`/v1/duo/${encodeURIComponent(code)}`)),
  duoAccept: async (code: string, selfie: Blob): Promise<DuoInvite> => {
    const form = new FormData();
    form.append('file', selfie, 'selfie.jpg');
    return duoInviteSchema.parse(await request(`/v1/duo/${encodeURIComponent(code)}/accept`, { method: 'POST', body: form }));
  },
  duoDecline: async (code: string): Promise<DuoInvite> => duoInviteSchema.parse(await request(`/v1/duo/${encodeURIComponent(code)}/decline`, { method: 'POST' })),
  duoCancel: async (code: string): Promise<DuoInvite> => duoInviteSchema.parse(await request(`/v1/duo/${encodeURIComponent(code)}/cancel`, { method: 'POST' })),
  events: async (events: ClientEventsRequest['events']): Promise<void> => {
    await request('/v1/events', { method: 'POST', body: JSON.stringify({ events }) });
  },
  report: async (body: ReportRequest): Promise<{ ok: boolean }> =>
    (await request('/v1/reports', { method: 'POST', body: JSON.stringify(body) })) as { ok: boolean },
  upload: async (file: Blob): Promise<Upload> => {
    const form = new FormData();
    form.append('file', file);
    return uploadSchema.parse(await request('/v1/uploads', { method: 'POST', body: form }));
  },
};
